import { FormEvent, useEffect, useState } from "react";
import {
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  MessageSquareText,
  Loader2,
} from "lucide-react";
import { Central, Entrada, TelemetriaFormData } from "./../types/types";
import styles from "./TelemetriaModal.module.css";

const API_URL = process.env.REACT_APP_API || "https://localhost:5001/api";

interface Props {
  id: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

const criarEntradasVazias = (): Entrada[] =>
  Array.from({ length: 16 }, (_, i) => ({
    numero: i + 1,
    tipo: "A",
    dispositivo: "",
    marcaModelo: "",
    adicional: "",
    info: "",
    localInstalacao: "",
    comentario: "",
  }));

const criarCentralVazia = (): Central => ({
  idCentral: "",
  entradas: criarEntradasVazias(),
});

const formInicial: TelemetriaFormData = {
  identificacao: "",
  endereco: "",
  contato: "",
  patrimonioIkone: "",
  operadora: "",
  informacoes: "",
  responsavel: "",
  dataInstalacao: null,
  centrais: [criarCentralVazia()],
};

export default function TelemetriaFormModal({ id, onClose, onSuccess }: Props) {
  const [form, setForm] = useState<TelemetriaFormData>(formInicial);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [expandedCentral, setExpandedCentral] = useState(0);
  const [openComment, setOpenComment] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/api/Telemetria/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Erro ${res.status} ao buscar telemetria`);
        return res.json();
      })
      .then((data) =>
        setForm({
          identificacao: data.identificacao,
          endereco: data.endereco,
          contato: data.contato,
          patrimonioIkone: data.patrimonioIkone,
          operadora: data.operadora,
          informacoes: data.informacoes,
          responsavel: data.responsavel,
          dataInstalacao: data.dataInstalacao,
          centrais: data.centrais.length
            ? data.centrais
            : [criarCentralVazia()],
        }),
      )
      .catch((err) => console.error("Erro ao carregar telemetria", err))
      .finally(() => setLoading(false));
  }, [id]);

  const atualizarCampo = (campo: keyof TelemetriaFormData, valor: string) =>
    setForm((prev) => ({ ...prev, [campo]: valor === "" ? null : valor }));

  const atualizarCentral = (idx: number, idCentral: string) =>
    setForm((prev) => {
      const centrais = [...prev.centrais];
      centrais[idx] = { ...centrais[idx], idCentral };
      return { ...prev, centrais };
    });

  const atualizarEntrada = (
    centralIdx: number,
    entradaIdx: number,
    campo: keyof Entrada,
    valor: string,
  ) =>
    setForm((prev) => {
      const centrais = [...prev.centrais];
      const entradas = [...centrais[centralIdx].entradas];
      entradas[entradaIdx] = { ...entradas[entradaIdx], [campo]: valor };
      centrais[centralIdx] = { ...centrais[centralIdx], entradas };
      return { ...prev, centrais };
    });

  const adicionarCentral = () =>
    setForm((prev) => {
      const centrais = [...prev.centrais, criarCentralVazia()];
      setExpandedCentral(centrais.length - 1);
      return { ...prev, centrais };
    });

  const removerCentral = (idx: number) =>
    setForm((prev) => ({
      ...prev,
      centrais: prev.centrais.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/Telemetria${id ? `/${id}` : ""}`,
        {
          method: id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      if (!res.ok) throw new Error(`Erro ${res.status} ao salvar telemetria`);
      onSuccess();
    } catch (err) {
      console.error("Erro ao salvar telemetria", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>

        <span className={styles.eyebrow}>
          {id ? "Editar telemetria" : "Nova telemetria"}
        </span>
        <h2 className={styles.modalTitle}>
          {id ? form.identificacao || "Editar" : "Cadastrar telemetria"}
        </h2>

        {loading ? (
          <div className={styles.loadingState}>
            <Loader2 size={22} className={styles.spinner} />
            Carregando dados...
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Identificação *</label>
                <input
                  className={styles.input}
                  value={form.identificacao}
                  onChange={(e) =>
                    atualizarCampo("identificacao", e.target.value)
                  }
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Contato</label>
                <input
                  className={styles.input}
                  value={form.contato}
                  onChange={(e) => atualizarCampo("contato", e.target.value)}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Responsável</label>
                <input
                  className={styles.input}
                  value={form.responsavel}
                  onChange={(e) =>
                    atualizarCampo("responsavel", e.target.value)
                  }
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Data da instalação</label>
                <input
                  type="date"
                  className={styles.input}
                  value={form.dataInstalacao ?? ""}
                  onChange={(e) =>
                    atualizarCampo("dataInstalacao", e.target.value)
                  }
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Endereço *</label>
              <input
                className={styles.input}
                value={form.endereco}
                onChange={(e) => atualizarCampo("endereco", e.target.value)}
                required
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Patrimônio Ikone</label>
                <input
                  className={styles.input}
                  value={form.patrimonioIkone}
                  onChange={(e) =>
                    atualizarCampo("patrimonioIkone", e.target.value)
                  }
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Operadora</label>
                <input
                  className={styles.input}
                  value={form.operadora}
                  onChange={(e) => atualizarCampo("operadora", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Informações</label>
                <input
                  className={styles.input}
                  placeholder="Ex: VIVO 4G"
                  value={form.informacoes}
                  onChange={(e) =>
                    atualizarCampo("informacoes", e.target.value)
                  }
                />
              </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.centraisHeader}>
              <span className={styles.sectionTitle}>Centrais</span>
              <button
                type="button"
                className={styles.addCentralBtn}
                onClick={adicionarCentral}
              >
                <Plus size={14} /> Adicionar central
              </button>
            </div>

            {form.centrais.map((central, centralIdx) => (
              <div key={centralIdx} className={styles.centralEditBlock}>
                <div
                  className={styles.centralEditHeader}
                  onClick={() =>
                    setExpandedCentral(
                      expandedCentral === centralIdx ? -1 : centralIdx,
                    )
                  }
                >
                  <input
                    className={styles.centralIdInput}
                    placeholder="ID Central (ex: 53)"
                    value={central.idCentral}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      atualizarCentral(centralIdx, e.target.value)
                    }
                    required
                  />

                  <div className={styles.centralEditActions}>
                    {form.centrais.length > 1 && (
                      <button
                        type="button"
                        className={styles.iconBtnDanger}
                        onClick={(e) => {
                          e.stopPropagation();
                          removerCentral(centralIdx);
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                    {expandedCentral === centralIdx ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </div>
                </div>

                {expandedCentral === centralIdx && (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Entrada</th>
                          <th>Tipo</th>
                          <th>Dispositivo</th>
                          <th>Marca/Modelo</th>
                          <th>Adicional</th>
                          <th>Info</th>
                          <th>Local instalação</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {central.entradas.map((entrada, entradaIdx) => {
                          const commentKey = `${centralIdx}-${entradaIdx}`;
                          return (
                            <tr key={entradaIdx}>
                              <td className={styles.entradaNumero}>
                                {entrada.numero}
                              </td>
                              <td>
                                <select
                                  className={styles.selectSm}
                                  value={entrada.tipo}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "tipo",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="A">A</option>
                                  <option value="D">D</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  className={styles.inputSm}
                                  value={entrada.dispositivo}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "dispositivo",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.inputSm}
                                  value={entrada.marcaModelo}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "marcaModelo",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.inputSm}
                                  value={entrada.adicional}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "adicional",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.inputSm}
                                  value={entrada.info}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "info",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className={styles.inputSm}
                                  value={entrada.localInstalacao}
                                  onChange={(e) =>
                                    atualizarEntrada(
                                      centralIdx,
                                      entradaIdx,
                                      "localInstalacao",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <div className={styles.commentWrap}>
                                  <button
                                    type="button"
                                    className={`${styles.commentToggle} ${
                                      entrada.comentario
                                        ? styles.commentToggleActive
                                        : ""
                                    }`}
                                    title="Comentário (opcional)"
                                    onClick={() =>
                                      setOpenComment(
                                        openComment === commentKey
                                          ? null
                                          : commentKey,
                                      )
                                    }
                                  >
                                    <MessageSquareText size={14} />
                                  </button>
                                  {openComment === commentKey && (
                                    <textarea
                                      className={styles.commentBox}
                                      placeholder="Comentário opcional para esta entrada..."
                                      value={entrada.comentario}
                                      onChange={(e) =>
                                        atualizarEntrada(
                                          centralIdx,
                                          entradaIdx,
                                          "comentario",
                                          e.target.value,
                                        )
                                      }
                                      autoFocus
                                    />
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={saving}
            >
              {saving ? (
                <Loader2 size={16} className={styles.spinner} />
              ) : id ? (
                "Salvar alterações"
              ) : (
                "Cadastrar telemetria"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
